import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled, useFeatureFlagVariantKey } from 'posthog-js/react';
import { FlexCenHorVer } from '@/UI/styles';
import { MenuAction } from '@/components/Menu/Actions';
import ParentMenuItem from '@/components/Recipe/Menu/ParentMenuItem';
import { AppMenuItem } from '@/UI/AppContextMenu/AppMenu.styles';
import { CheckMarkIcon } from '@/UI/Icons/CheckMarkIcon';
import { I18N_KEYS } from '@/language/keys';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { SubscriptionType } from '@/types/shared';
import {
  FF_DISABLE_SHARE_FOR_FREE_USERS,
  FF_DISABLE_SHARE_FOR_FREE_USERS_VARIANT_TEST,
  FF_KEY_NAVIGATION,
} from '@/consts/featureFlags';
import { useWorkflowStore } from '@/state/workflow.state';
import { color } from '@/colors';
import { useGlobalStore } from '@/state/global.state';
import { getOS } from '@/utils/general';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { useCreateRecipe } from '@/queries/useRecipes';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { useFigmaEmbedPermissions, useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';
import { OpenRecentMenuItem } from './OpenRecentMenuItem';
import { EdgeTypeMenu } from './EdgeTypeMenu';
import type { EdgeTypePreference } from '@/types/auth.types';

// Figma editor-type strings the parent recognizes on `WEAVE_CREATE_FILE`.
// Mirrors `FigFile::EditorType` serialize values on the Sinatra side, but the
// Figma frontend ultimately translates them into the canonical `EditorType`
// enum before dispatching `createNewFile`.
//
// TODO(weave): the original design also exposed `New ▶ {Design, FigJam, Slides,
// Make, Sites}`. Cross-editor file creation requires the Figma parent to gate
// on the appropriate per-editor `CAN_CREATE_*` permissions; until that wiring
// lands we restrict this menu to `New Weave` only.
type FigmaCreateEditorType = 'weave';

function postCreateFileToParent(editorType: FigmaCreateEditorType) {
  try {
    window.parent.postMessage({ type: 'WEAVE_CREATE_FILE', editorType }, '*');
  } catch {
    // Parent postMessage blocked — give up silently rather than crash the
    // menu click handler. Same posture as `WEAVE_BACK_TO_FILES`.
  }
}

interface WorkflowFileMenuProps {
  isMenuOpen: boolean;
  goToDashboard: () => void;
  onDuplicateRecipe: (recipeId: string) => void;
  panOnScrollEnabled?: boolean;
  onPanOnScrollChange?: (enabled: boolean) => void;
  showFloatMenuOnRightClickEnabled?: boolean;
  onShowFloatMenuOnRightClickChange?: (enabled: boolean) => void;
  requireAltKeyForSuggestionsEnabled?: boolean;
  onRequireAltKeyForSuggestionsChange?: (enabled: boolean) => void;
  snapToGridEnabled?: boolean;
  onSnapToGridChange?: (enabled: boolean) => void;
  edgeType?: EdgeTypePreference;
  onEdgeTypeChange?: (value: EdgeTypePreference) => void;
  onShareWorkflow: () => void;
}

function WorkflowFileMenu({
  isMenuOpen,
  goToDashboard,
  onDuplicateRecipe,
  panOnScrollEnabled,
  onPanOnScrollChange,
  showFloatMenuOnRightClickEnabled,
  onShowFloatMenuOnRightClickChange,
  requireAltKeyForSuggestionsEnabled,
  onRequireAltKeyForSuggestionsChange,
  snapToGridEnabled,
  onSnapToGridChange,
  edgeType,
  onEdgeTypeChange,
  onShareWorkflow,
}: WorkflowFileMenuProps) {
  const [anchorElUserMenu, setAnchorElUserMenu] = useState<HTMLElement | null>(null);
  const { t } = useTranslation();
  const isFigmaEmbedMode = useIsFigmaEmbedMode();
  // Permission for `New Weave` is owned by the Figma parent (LiveGraph
  // FileCreationPermissionsView). It pushes the boolean over postMessage; we
  // read it here to disable the menu item, matching how Figma's own
  // `File ▶ New ▶ {…}` items get `disabled = !canCreate`.
  // `null` = parent hasn't replied yet — treat as disabled, not optimistic.
  const { canCreateWeaveFile } = useFigmaEmbedPermissions();
  const isNewWeaveDisabled = isFigmaEmbedMode && canCreateWeaveFile !== true;
  const { isSubscriptionTypeIn } = useSubscriptionPermissions();
  const disableShareExperimentVariant = useFeatureFlagVariantKey(FF_DISABLE_SHARE_FOR_FREE_USERS);
  const isSharingDisabled =
    disableShareExperimentVariant === FF_DISABLE_SHARE_FOR_FREE_USERS_VARIANT_TEST &&
    isSubscriptionTypeIn([SubscriptionType.Free]);
  const currentRecipeId = useWorkflowStore((state) => state.recipe.id);
  const { mutate: createRecipe } = useCreateRecipe();
  const setIsEditingNameOnToolbar = useWorkflowStore((state) => state.setIsEditingNameOnToolbar);
  const setIsEditingNameOnWorkflow = useWorkflowStore((state) => state.setIsEditingNameOnWorkflow);

  const ff_isKeyNavigationEnabled = useFeatureFlagEnabled(FF_KEY_NAVIGATION);
  const setIsShortcutsPanelOpen = useGlobalStore((s) => s.setIsShortcutsPanelOpen);
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorElUserMenu(event.currentTarget);
  const isFileMenuOpen = Boolean(anchorElUserMenu);

  const getKeyNameByOs = useCallback(() => {
    const keyNameByOs = {
      Mac: 'Option',
      Windows: 'Alt',
      Linux: 'Option',
      Other: 'Option',
    };
    return keyNameByOs[getOS() ?? 'Other'];
  }, []);

  const handleCloseUserMenu = useCallback(() => setAnchorElUserMenu(null), []);

  const handleLogoClick = (event: React.MouseEvent<HTMLElement>) => {
    const isCtrlOrCmd = event.metaKey || event.ctrlKey;

    if (isCtrlOrCmd) {
      // Open dashboard in new tab
      event.preventDefault();
      event.stopPropagation();
      window.open('/', '_blank');
    } else {
      // Normal behavior - open menu
      handleOpenUserMenu(event);
    }
  };

  const handlePanOnScrollToggle = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (onPanOnScrollChange && panOnScrollEnabled !== undefined) {
        onPanOnScrollChange(!panOnScrollEnabled);
      }
    },
    [onPanOnScrollChange, panOnScrollEnabled],
  );

  const handleShowFloatMenuOnRightClickToggle = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (onShowFloatMenuOnRightClickChange && showFloatMenuOnRightClickEnabled !== undefined) {
        onShowFloatMenuOnRightClickChange(!showFloatMenuOnRightClickEnabled);
      }
    },
    [onShowFloatMenuOnRightClickChange, showFloatMenuOnRightClickEnabled],
  );

  const handleRequireAltKeyForSuggestionsToggle = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (onRequireAltKeyForSuggestionsChange && requireAltKeyForSuggestionsEnabled !== undefined) {
        onRequireAltKeyForSuggestionsChange(!requireAltKeyForSuggestionsEnabled);
      }
    },
    [onRequireAltKeyForSuggestionsChange, requireAltKeyForSuggestionsEnabled],
  );

  const handleSnapToGridToggle = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (onSnapToGridChange && snapToGridEnabled !== undefined) {
        onSnapToGridChange(!snapToGridEnabled);
      }
    },
    [onSnapToGridChange, snapToGridEnabled],
  );

  const menuItems: MenuAction[] = useMemo(() => {
    const items: MenuAction[] = [
      {
        name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.BACK_TO_DASHBOARD),
        action: (e: MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          if (isFigmaEmbedMode) {
            // Ask the Figma parent to run its canonical `fullscreenActions.backToFiles`
            // flow via postMessage, rather than navigating the top frame directly.
            // Parent handler validates origin + source before acting. See
            // `web/js/weave/weave_iframe_view.tsx` for the receiving side.
            try {
              window.parent.postMessage({ type: 'WEAVE_BACK_TO_FILES' }, '*');
            } catch {
              // parent postMessage blocked — give up silently rather than crash the
              // menu click handler.
            }
            return;
          }
          goToDashboard();
        },
      },
      // In Figma embed mode the surrounding Figma shell owns file lifecycle
      // (open recent / rename) and sharing, so hide those entries and
      // replace the file-lifecycle block with a `File ▶` submenu that
      // postMessages create-file requests to the Figma parent. Avoids
      // leaving two consecutive dividers around an empty section.
      ...(isFigmaEmbedMode
        ? ([
            {
              type: 'slot',
              el: (
                <ParentMenuItem
                  id="file"
                  label={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.FILE)}
                  onClose={handleCloseUserMenu}
                  contextMenuWidth="200px"
                  offset={{ left: '10px', top: '-9px' }}
                >
                  <AppMenuItem
                    disabled={isNewWeaveDisabled}
                    onClick={(e: MouseEvent<HTMLElement>) => {
                      e.stopPropagation();
                      postCreateFileToParent('weave');
                      handleCloseUserMenu();
                    }}
                  >
                    <Typography fontSize="inherit">
                      {t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.NEW_WEAVE)}
                    </Typography>
                  </AppMenuItem>
                </ParentMenuItem>
              ),
            },
          ] satisfies MenuAction[])
        : ([
            {
              type: 'divider',
            },
            {
              name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.CREATE_NEW_FILE),
              action: (e: MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                void createRecipe({ openInNewTab: true });
              },
            },
            {
              type: 'slot',
              el: <OpenRecentMenuItem currentRecipeId={currentRecipeId} onClose={handleCloseUserMenu} />,
            },
            {
              type: 'divider',
            },
            {
              name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.DUPLICATE_FILE),
              action: (e: MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                onDuplicateRecipe(currentRecipeId);
              },
            },
            {
              name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.RENAME_WORKFLOW),
              action: (e: MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                handleCloseUserMenu();
                if (isMenuOpen) {
                  setIsEditingNameOnToolbar(true);
                } else {
                  setIsEditingNameOnWorkflow(true);
                }
              },
            },
            {
              name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.SHARE_WORKFLOW),
              action: (e: MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                onShareWorkflow();
              },
              disabled: isSharingDisabled,
            },
          ] satisfies MenuAction[])),
      {
        type: 'divider',
      },
      ...(ff_isKeyNavigationEnabled
        ? [
            {
              name: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.KEYBOARD_SHORTCUTS),
              action: (e: MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                setIsShortcutsPanelOpen(true);
              },
            },
          ]
        : []),
      {
        type: 'slot',
        el: (
          <ParentMenuItem
            id="preferences"
            label={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.PREFERENCES)}
            onClose={handleCloseUserMenu}
            contextMenuWidth="260px"
            offset={{ left: '10px', top: '-9px' }}
          >
            <AppMenuItem onClick={handlePanOnScrollToggle}>
              <FlexCenHorVer sx={{ justifyContent: 'space-between', width: '100%' }}>
                <Typography fontSize="inherit">
                  {t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.USE_WHEEL_TO_ZOOM)}
                </Typography>
                {!panOnScrollEnabled && <CheckMarkIcon />}
              </FlexCenHorVer>
            </AppMenuItem>
            <AppMenuItem onClick={handleShowFloatMenuOnRightClickToggle}>
              <FlexCenHorVer sx={{ justifyContent: 'space-between', width: '100%' }}>
                <Typography fontSize="inherit">
                  {t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.RIGHT_CLICK_TO_OPEN_MENU)}
                </Typography>
                {showFloatMenuOnRightClickEnabled && <CheckMarkIcon />}
              </FlexCenHorVer>
            </AppMenuItem>
            <AppMenuItem onClick={handleRequireAltKeyForSuggestionsToggle}>
              <FlexCenHorVer sx={{ justifyContent: 'space-between', width: '100%' }}>
                <Typography fontSize="inherit">
                  {t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.REQUIRE_ALT_KEY_FOR_SUGGESTIONS, {
                    key: `${getKeyNameByOs()}`,
                  })}
                </Typography>
                {requireAltKeyForSuggestionsEnabled && <CheckMarkIcon />}
              </FlexCenHorVer>
            </AppMenuItem>
            {snapToGridEnabled !== undefined && onSnapToGridChange && (
              <AppMenuItem onClick={handleSnapToGridToggle}>
                <FlexCenHorVer sx={{ justifyContent: 'space-between', width: '100%' }}>
                  <Typography fontSize="inherit">
                    {t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_TOOLS_PANEL.MAIN_MENU.SNAP_TO_GRID)}
                  </Typography>
                  {snapToGridEnabled && <CheckMarkIcon />}
                </FlexCenHorVer>
              </AppMenuItem>
            )}
            {edgeType !== undefined && onEdgeTypeChange && (
              <EdgeTypeMenu edgeType={edgeType} onEdgeTypeChange={onEdgeTypeChange} onClose={handleCloseUserMenu} />
            )}
          </ParentMenuItem>
        ),
      },
    ];

    return items;
  }, [
    t,
    ff_isKeyNavigationEnabled,
    goToDashboard,
    createRecipe,
    onDuplicateRecipe,
    currentRecipeId,
    onShareWorkflow,
    setIsShortcutsPanelOpen,
    isMenuOpen,
    setIsEditingNameOnToolbar,
    setIsEditingNameOnWorkflow,
    handlePanOnScrollToggle,
    handleShowFloatMenuOnRightClickToggle,
    handleRequireAltKeyForSuggestionsToggle,
    handleSnapToGridToggle,
    handleCloseUserMenu,
    getKeyNameByOs,
    isNewWeaveDisabled,
    panOnScrollEnabled,
    requireAltKeyForSuggestionsEnabled,
    showFloatMenuOnRightClickEnabled,
    snapToGridEnabled,
    onSnapToGridChange,
    edgeType,
    onEdgeTypeChange,
    isSharingDisabled,
    isFigmaEmbedMode,
  ]);

  return (
    <>
      <AppIconButton
        sx={{ mb: 4, backgroundColor: isFileMenuOpen ? color.Black84 : undefined }}
        width={36}
        height={36}
        onClick={handleLogoClick}
      >
        {isFigmaEmbedMode ? (
          <img src="/icons/figma-logo.svg" alt="Figma" width={24} />
        ) : (
          <img src="/icons/logo.svg" alt="Logo" width={24} />
        )}
      </AppIconButton>
      <AppContextMenu
        offset={{ top: '4px', left: '-6px' }}
        open={isFileMenuOpen}
        onClose={handleCloseUserMenu}
        items={menuItems}
        width="180px"
        anchorEl={anchorElUserMenu}
        keepMounted
      />
    </>
  );
}

export default WorkflowFileMenu;
